import React from "react";
import { Link } from "react-router-dom";

/**
 * Simple onboarding checklist shown on the Dashboard while the user hasn't
 * connected LinkedIn or created a segment yet.
 */
export default function OnboardingChecklist({
  linkedInConnected = false,
  segmentsCount = 0,
  activeSegmentsCount = 0,
  plan = "freemium",
}) {
  const steps = [
    {
      number: 1,
      label: "Connecter LinkedIn",
      done: linkedInConnected,
      hint: "Autorisez MmeValentine à agir depuis votre compte LinkedIn.",
      to: null,
    },
    {
      number: 2,
      label: "Créer un segment",
      done: segmentsCount > 0,
      hint: "Définissez votre audience cible et les instructions pour l'IA.",
      to: "/segments/new",
    },
    {
      number: 3,
      label: "Activer",
      done: activeSegmentsCount > 0,
      hint: "Passez le segment en statut « Actif » pour lancer l'envoi automatique.",
      to: "/segments",
    },
  ];

  const isFreemium = (plan || "freemium").toLowerCase() !== "premium";
  const allDone = steps.every((step) => step.done);

  if (allDone) return null;

  return (
    <div className="card p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="type-section-title text-ink">Bien démarrer</h2>
          <p className="text-sm text-mute mt-1">
            Trois étapes pour que l'IA commence à travailler pour vous.
          </p>
        </div>
      </div>

      <ol className="space-y-3">
        {steps.map((step) => {
          const content = (
            <div className="flex items-start gap-3">
              <span
                className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                  step.done
                    ? "bg-emerald-500 text-white"
                    : "bg-ink/5 text-ink/50 border border-line"
                }`}
              >
                {step.done ? (
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  step.number
                )}
              </span>
              <div className="flex-1 min-w-0">
                <p
                  className={`text-sm font-medium ${
                    step.done ? "text-ink/50 line-through" : "text-ink"
                  }`}
                >
                  {step.label}
                </p>
                <p className="text-xs text-mute mt-0.5">{step.hint}</p>
              </div>
            </div>
          );

          return (
            <li
              key={step.number}
              className="rounded-lg border border-line px-3 py-3 hover:border-ink/20 transition-colors"
            >
              {!step.done && step.to ? (
                <Link to={step.to} className="block">
                  {content}
                </Link>
              ) : (
                content
              )}
            </li>
          );
        })}
      </ol>

      {isFreemium && (
        <div className="mt-4 pt-4 border-t border-line text-center">
          <Link
            to="/pricing"
            className="text-sm font-medium text-rouge hover:underline underline-offset-2"
          >
            Passer à Premium pour débloquer plus de fonctionnalités →
          </Link>
        </div>
      )}
    </div>
  );
}
